/**
 * Filter-state shaping for the jobs list's Ops lens (WorkPortfolioPage /
 * JobsFilterDrawer). Pure functions only — no DOM, no react-router — so the
 * URL <-> state <-> adapter-options mapping is defined once and is testable
 * without mounting the page. See
 * docs/features/jobs-filter-system/01_product_brief.md for the catalogue
 * this encodes.
 */
import type * as API from "../data/api-types";
import type {
  WorkPortfolioDueWindow,
  WorkPortfolioListOptions,
  WorkPortfolioQuantityBand,
  WorkPortfolioRepeatFlag,
  WorkPortfolioValueBand,
} from "../data/adapter";
import { formatCount } from "./formatters";

export interface JobsFilterValues {
  mine: boolean;
  viewingAsUserId: string;
  csr: string[];
  salesRep: string[];
  exceptionCodes: string[];
  unacknowledgedOnly: boolean;
  hasIssues: boolean;
  complaints: boolean;
  /** "Only jobs with alerts" (product brief §17 D4). Its *initial* value is
   *  computed once at mount from whether the inbound URL named any other
   *  filter of its own — see `computeDefaultAlertsOnly` — then behaves as an
   *  ordinary explicit toggle from that point on. */
  alertsOnly: boolean;
  readiness: API.Readiness | "";
  stageCode: string;
  statuses: string[];
  dueWindow: WorkPortfolioDueWindow | "";
  dueAfter: string;
  dueBefore: string;
  bookedAfter: string;
  bookedBefore: string;
  /** Kept as the raw input string; parse with `Number` at the adapter-call
   *  boundary. Empty string = unset. */
  waitingMoreThanDays: string;
  jobTypeCode: string[];
  repeatFlags: WorkPortfolioRepeatFlag[];
  productionSite: string[];
  despatchMethod: string[];
  market: string[];
  valueBand: WorkPortfolioValueBand[];
  quantityBand: WorkPortfolioQuantityBand[];
  inSeries: boolean;
}

export function emptyJobsFilterValues(): JobsFilterValues {
  return {
    mine: false,
    viewingAsUserId: "",
    csr: [],
    salesRep: [],
    exceptionCodes: [],
    unacknowledgedOnly: false,
    hasIssues: false,
    complaints: false,
    alertsOnly: false,
    readiness: "",
    stageCode: "",
    statuses: [],
    dueWindow: "",
    dueAfter: "",
    dueBefore: "",
    bookedAfter: "",
    bookedBefore: "",
    waitingMoreThanDays: "",
    jobTypeCode: [],
    repeatFlags: [],
    productionSite: [],
    despatchMethod: [],
    market: [],
    valueBand: [],
    quantityBand: [],
    inSeries: false,
  };
}

// Every URL param this module owns, besides `alerts_only` itself — used
// both to parse values out and to decide whether the inbound URL named any
// filter of its own (see `computeDefaultAlertsOnly`). `customer` and
// `series_parent_job_no` are page-level params owned by WorkPortfolioPage,
// not the drawer, but they still count as "this link already narrows the
// list" for the D4 default-suppression rule.
const FILTER_PARAM_KEYS = [
  "mine",
  "viewing_as",
  "csr",
  "sales_rep",
  "exception_codes",
  "unacknowledged_only",
  "has_issues",
  "complaints",
  "readiness",
  "stage_code",
  "statuses",
  "due_window",
  "due_after",
  "due_before",
  "booked_after",
  "booked_before",
  "waiting_more_than_days",
  "job_type_code",
  "repeat_flag",
  "production_site",
  "despatch_method",
  "market",
  "value_band",
  "quantity_band",
  "in_series",
  "customer",
  "series_parent_job_no",
] as const;

/** True when the inbound URL named at least one filter of its own — used to
 *  decide whether the D4 "hide jobs with no alerts" default should apply.
 *  A link promising a specific filtered population (CSR home's readiness
 *  links, a customer deep link, …) must never be narrowed further by a
 *  silent default on top. */
export function computeDefaultAlertsOnly(params: URLSearchParams): boolean {
  return !FILTER_PARAM_KEYS.some((key) => params.has(key));
}

function csv(params: URLSearchParams, key: string): string[] {
  return params.getAll(key).filter(Boolean);
}

export function parseJobsFilterValues(
  params: URLSearchParams,
  defaultAlertsOnly: boolean,
): JobsFilterValues {
  const boolParam = (key: string) => params.get(key) === "1";
  return {
    mine: boolParam("mine"),
    viewingAsUserId: params.get("viewing_as") ?? "",
    csr: csv(params, "csr"),
    salesRep: csv(params, "sales_rep"),
    exceptionCodes: csv(params, "exception_codes"),
    unacknowledgedOnly: boolParam("unacknowledged_only"),
    hasIssues: boolParam("has_issues"),
    complaints: boolParam("complaints"),
    alertsOnly:
      params.get("alerts_only") !== null
        ? params.get("alerts_only") === "1"
        : defaultAlertsOnly,
    readiness: (params.get("readiness") as API.Readiness | null) ?? "",
    stageCode: params.get("stage_code") ?? "",
    statuses: csv(params, "statuses"),
    dueWindow:
      (params.get("due_window") as WorkPortfolioDueWindow | null) ?? "",
    dueAfter: params.get("due_after") ?? "",
    dueBefore: params.get("due_before") ?? "",
    bookedAfter: params.get("booked_after") ?? "",
    bookedBefore: params.get("booked_before") ?? "",
    waitingMoreThanDays: params.get("waiting_more_than_days") ?? "",
    jobTypeCode: csv(params, "job_type_code"),
    repeatFlags: csv(params, "repeat_flag") as WorkPortfolioRepeatFlag[],
    productionSite: csv(params, "production_site"),
    despatchMethod: csv(params, "despatch_method"),
    market: csv(params, "market"),
    valueBand: csv(params, "value_band") as WorkPortfolioValueBand[],
    quantityBand: csv(params, "quantity_band") as WorkPortfolioQuantityBand[],
    inSeries: boolParam("in_series"),
  };
}

/** Mirrors `filters` onto `params` in place, omitting each key when it's at
 *  its empty/default value — the `setOrDelete` pattern from TicketsPage's
 *  URL-sync effect. `alerts_only` is the one field written explicitly
 *  whenever it differs from the mount-computed default, since "default" is
 *  context-dependent (see `computeDefaultAlertsOnly`) rather than a fixed
 *  constant. */
export function writeJobsFilterValues(
  params: URLSearchParams,
  filters: JobsFilterValues,
  defaultAlertsOnly: boolean,
): void {
  const setOrDelete = (key: string, value: string | null) => {
    if (value) params.set(key, value);
    else params.delete(key);
  };
  const setOrDeleteList = (key: string, values: string[]) => {
    params.delete(key);
    for (const v of values) params.append(key, v);
  };
  setOrDelete("mine", filters.mine ? "1" : null);
  setOrDelete("viewing_as", filters.viewingAsUserId || null);
  setOrDeleteList("csr", filters.csr);
  setOrDeleteList("sales_rep", filters.salesRep);
  setOrDeleteList("exception_codes", filters.exceptionCodes);
  setOrDelete("unacknowledged_only", filters.unacknowledgedOnly ? "1" : null);
  setOrDelete("has_issues", filters.hasIssues ? "1" : null);
  setOrDelete("complaints", filters.complaints ? "1" : null);
  setOrDelete(
    "alerts_only",
    filters.alertsOnly !== defaultAlertsOnly
      ? filters.alertsOnly
        ? "1"
        : "0"
      : null,
  );
  setOrDelete("readiness", filters.readiness || null);
  setOrDelete("stage_code", filters.stageCode || null);
  setOrDeleteList("statuses", filters.statuses);
  setOrDelete("due_window", filters.dueWindow || null);
  setOrDelete(
    "due_after",
    filters.dueWindow === "custom" ? filters.dueAfter || null : null,
  );
  setOrDelete(
    "due_before",
    filters.dueWindow === "custom" ? filters.dueBefore || null : null,
  );
  setOrDelete("booked_after", filters.bookedAfter || null);
  setOrDelete("booked_before", filters.bookedBefore || null);
  setOrDelete("waiting_more_than_days", filters.waitingMoreThanDays || null);
  setOrDeleteList("job_type_code", filters.jobTypeCode);
  setOrDeleteList("repeat_flag", filters.repeatFlags);
  setOrDeleteList("production_site", filters.productionSite);
  setOrDeleteList("despatch_method", filters.despatchMethod);
  setOrDeleteList("market", filters.market);
  setOrDeleteList("value_band", filters.valueBand);
  setOrDeleteList("quantity_band", filters.quantityBand);
  setOrDelete("in_series", filters.inSeries ? "1" : null);
}

export function jobsFilterValuesToListOptions(
  filters: JobsFilterValues,
): Partial<WorkPortfolioListOptions> {
  const options: Partial<WorkPortfolioListOptions> = {};
  if (filters.mine) options.mine = true;
  if (filters.viewingAsUserId)
    options.scopeOverrideUserId = filters.viewingAsUserId;
  if (filters.csr.length) options.csr = filters.csr;
  if (filters.salesRep.length) options.salesRep = filters.salesRep;
  if (filters.exceptionCodes.length)
    options.exceptionCodes = filters.exceptionCodes;
  if (filters.unacknowledgedOnly) options.unacknowledgedOnly = true;
  if (filters.hasIssues) options.hasIssues = true;
  if (filters.complaints) options.complaints = true;
  if (filters.alertsOnly) options.alertsOnly = true;
  if (filters.readiness) options.readiness = filters.readiness;
  if (filters.stageCode) options.stageCode = filters.stageCode;
  if (filters.statuses.length) options.statuses = filters.statuses;
  if (filters.dueWindow) {
    options.dueWindow = filters.dueWindow;
    if (filters.dueWindow === "custom") {
      if (filters.dueAfter) options.dueAfter = filters.dueAfter;
      if (filters.dueBefore) options.dueBefore = filters.dueBefore;
    }
  }
  if (filters.bookedAfter) options.bookedAfter = filters.bookedAfter;
  if (filters.bookedBefore) options.bookedBefore = filters.bookedBefore;
  const waitingDays = Number(filters.waitingMoreThanDays);
  if (
    filters.waitingMoreThanDays &&
    Number.isFinite(waitingDays) &&
    waitingDays > 0
  ) {
    options.waitingMoreThanDays = waitingDays;
  }
  if (filters.jobTypeCode.length) options.jobTypeCode = filters.jobTypeCode;
  if (filters.repeatFlags.length) options.repeatFlags = filters.repeatFlags;
  if (filters.productionSite.length)
    options.productionSite = filters.productionSite;
  if (filters.despatchMethod.length)
    options.despatchMethod = filters.despatchMethod;
  if (filters.market.length) options.market = filters.market;
  if (filters.valueBand.length) options.valueBand = filters.valueBand;
  if (filters.quantityBand.length) options.quantityBand = filters.quantityBand;
  if (filters.inSeries) options.inSeries = true;
  return options;
}

export interface JobsFilterChipDescriptor {
  /** Stable key, so the shell can build the right "remove just this one"
   *  patch without re-deriving which axis/value it came from. */
  key: string;
  label: string;
}

export interface JobsFilterLabelContext {
  exceptionCodeLabel: (code: string) => string;
  stageLabel: (code: string) => string;
  dueWindowLabel: (window: WorkPortfolioDueWindow) => string;
  valueBandLabel: (band: WorkPortfolioValueBand) => string;
  quantityBandLabel: (band: WorkPortfolioQuantityBand) => string;
  readinessLabel: (readiness: API.Readiness) => string;
  statusLabel: (status: string) => string;
  /** Resolved display name for `viewingAsUserId`, or null while unresolved. */
  viewingAsName: string | null;
}

/** One descriptor per active filter *value* — this is what both the
 *  "Filters (N)" trigger count and the active-filter chip row render from,
 *  so the two can never drift out of sync with each other. */
export function describeActiveJobsFilters(
  filters: JobsFilterValues,
  labels: JobsFilterLabelContext,
): JobsFilterChipDescriptor[] {
  const chips: JobsFilterChipDescriptor[] = [];
  if (filters.alertsOnly) {
    chips.push({ key: "alertsOnly", label: "Only jobs with alerts" });
  }
  if (filters.mine) chips.push({ key: "mine", label: "Mine" });
  if (filters.viewingAsUserId) {
    chips.push({
      key: "viewingAsUserId",
      label: `Viewing as: ${labels.viewingAsName ?? "…"}`,
    });
  }
  for (const v of filters.csr)
    chips.push({ key: `csr:${v}`, label: `CSR: ${v}` });
  for (const v of filters.salesRep)
    chips.push({ key: `salesRep:${v}`, label: `Sales rep: ${v}` });
  for (const v of filters.exceptionCodes)
    chips.push({
      key: `exceptionCodes:${v}`,
      label: labels.exceptionCodeLabel(v),
    });
  if (filters.unacknowledgedOnly)
    chips.push({
      key: "unacknowledgedOnly",
      label: "Only unacknowledged alerts",
    });
  if (filters.hasIssues)
    chips.push({ key: "hasIssues", label: "Has open issues" });
  if (filters.complaints)
    chips.push({ key: "complaints", label: "Has complaints" });
  if (filters.readiness)
    chips.push({
      key: "readiness",
      label: `Readiness: ${labels.readinessLabel(filters.readiness)}`,
    });
  if (filters.stageCode)
    chips.push({
      key: "stageCode",
      label: `Stage: ${labels.stageLabel(filters.stageCode)}`,
    });
  for (const v of filters.statuses)
    chips.push({
      key: `statuses:${v}`,
      label: `Status: ${labels.statusLabel(v)}`,
    });
  if (filters.dueWindow) {
    const label =
      filters.dueWindow === "custom" && (filters.dueAfter || filters.dueBefore)
        ? `Due: ${filters.dueAfter || "…"} – ${filters.dueBefore || "…"}`
        : `Due: ${labels.dueWindowLabel(filters.dueWindow)}`;
    chips.push({ key: "dueWindow", label });
  }
  if (filters.bookedAfter || filters.bookedBefore) {
    chips.push({
      key: "bookedWindow",
      label: `Booked: ${filters.bookedAfter || "…"} – ${filters.bookedBefore || "…"}`,
    });
  }
  if (filters.waitingMoreThanDays) {
    chips.push({
      key: "waitingMoreThanDays",
      label: `Waiting more than ${filters.waitingMoreThanDays} days`,
    });
  }
  for (const v of filters.jobTypeCode)
    chips.push({ key: `jobTypeCode:${v}`, label: `Product: ${v}` });
  for (const v of filters.repeatFlags)
    chips.push({
      key: `repeatFlags:${v}`,
      label: v === "new" ? "New work" : "Repeat work",
    });
  for (const v of filters.productionSite)
    chips.push({ key: `productionSite:${v}`, label: `Production site: ${v}` });
  for (const v of filters.despatchMethod)
    chips.push({ key: `despatchMethod:${v}`, label: `Despatch: ${v}` });
  for (const v of filters.market)
    chips.push({ key: `market:${v}`, label: `Market: ${v}` });
  for (const v of filters.valueBand)
    chips.push({
      key: `valueBand:${v}`,
      label: `Value: ${labels.valueBandLabel(v)}`,
    });
  for (const v of filters.quantityBand)
    chips.push({
      key: `quantityBand:${v}`,
      label: `Quantity: ${labels.quantityBandLabel(v)}`,
    });
  if (filters.inSeries)
    chips.push({ key: "inSeries", label: "Part of a series" });
  return chips;
}

/** Remove exactly the value a chip's `key` identifies, returning a new
 *  `JobsFilterValues`. Handles scalar axes (key === field name), multi-select
 *  axes (key === "field:value"), and the two synthetic combined-range keys
 *  ("dueWindow" also clears its custom bounds; "bookedWindow" clears both
 *  booked bounds at once, since neither bound is independently a chip). */
export function removeJobsFilterChip(
  filters: JobsFilterValues,
  key: string,
): JobsFilterValues {
  if (key === "dueWindow") {
    return { ...filters, dueWindow: "", dueAfter: "", dueBefore: "" };
  }
  if (key === "bookedWindow") {
    return { ...filters, bookedAfter: "", bookedBefore: "" };
  }
  const [field, value] = key.split(":") as [keyof JobsFilterValues, string?];
  if (value === undefined) {
    const scalarDefaults = emptyJobsFilterValues();
    return { ...filters, [field]: scalarDefaults[field] };
  }
  const current = filters[field];
  if (!Array.isArray(current)) return filters;
  return { ...filters, [field]: current.filter((v) => v !== value) };
}

/** The one-line count summary rendered above the jobs list, e.g.
 *  "12 of 147 jobs shown · 53 of those 147 with a warning · 4 of those
 *  147 overdue".
 *
 *  `shown` is `WorkPortfolioList.total` — the number of jobs the current
 *  request actually returns, across every filter including the RAG facet
 *  and the two alert-based toggles ("only jobs with alerts" and "only
 *  unacknowledged alerts"). `population` is `summary_cards.total_active` —
 *  the count over the same filters *excluding* those three narrowings (see
 *  `work_portfolio_query.py`'s cards-exclude-RAG rule, mirrored on the mock
 *  adapter). `warningCount` and `overdueCount` are always population
 *  tallies, never a count of the visible rows — they describe the wider set
 *  a RAG/alert-based filter is being applied within, which is the whole
 *  reason the population figure is kept alongside them.
 *
 *  When `shown === population` (neither of those two axes is narrowing
 *  anything), the population figure is dropped rather than repeating
 *  "N of N" — there is only one number to say. Otherwise every reference
 *  to the population is spelled out explicitly ("of those 147") rather
 *  than left to a pronoun, so a reader can never mistake a population
 *  tally for a count of what is on screen. Callers should suppress this
 *  line entirely when `shown` is 0 — the dedicated empty-state message
 *  already explains an empty list, and a "0 of N jobs" line alongside it
 *  would only repeat that in a less specific way. */
export function describePortfolioCountSummary(
  shown: number,
  population: number,
  warningCount: number,
  overdueCount: number,
): string {
  if (shown === population) {
    return (
      `${formatCount(population)} jobs · ` +
      `${formatCount(warningCount)} with a warning · ` +
      `${formatCount(overdueCount)} overdue`
    );
  }
  // The warning/overdue tallies describe the population being filtered
  // within, not the visible rows, so the population is named once up front
  // and the tallies hang off it. Repeating "of those N" per tally was
  // unambiguous but did not read like English, and this line is on screen
  // for every CSR on every page load.
  const ofPopulation = formatCount(population);
  return (
    `${formatCount(shown)} of ${ofPopulation} jobs shown · ` +
    `of those ${ofPopulation}: ${formatCount(warningCount)} with a warning, ` +
    `${formatCount(overdueCount)} overdue`
  );
}
